<template>
  <div class="container-fluid py-4">

    <!-- Header patient -->
    <div v-if="!isLoading && patient"
      class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-person-badge fs-2 text-primary"></i>
        <div>
          <h2 class="fw-bold mb-0">{{ patient.prenom }} {{ patient.nom }}</h2>
          <p class="mb-0 text-muted">Numéro dossier : {{ patient.numeroDossier }}</p>
        </div>
      </div>
      <div class="d-flex gap-2 flex-wrap align-items-center mt-2 mt-md-0">
        <DonneesPatient :patient="patient" />
        <router-link
          class="btn btn-primary btn-lg shadow-sm btn-clinique"
          :to="{ name: 'patients' }"
        >
          <i class="bi bi-arrow-left-circle me-2"></i> Retour
        </router-link>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="isLoading" class="text-center my-5">
      <div
        class="spinner-border text-primary"
        role="status"
        style="width: 4rem; height: 4rem;"
      >
        <span class="visually-hidden">Chargement...</span>
      </div>
      <p class="mt-2">Chargement des informations du patient...</p>
    </div>

    <!-- Patient details card -->
    <div v-else-if="patient" class="card shadow-sm rounded card-clinique mb-4">

      <!-- Onglets -->
      <div class="tabs-container mt-3 mb-3">
        <ul class="nav nav-tabs nav-clinique">
          <li class="nav-item" v-for="tab in tabsAutorises" :key="tab.route">
            <router-link
              class="nav-link"
              :class="{ active: isActive(tab.route) }"
              :to="{ name: tab.route, params: { id: patientId } }"
            >
              {{ tab.label }}
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Contenu onglet -->
      <div class="table-responsive">
        <router-view :patient-id="patientId"></router-view>
      </div>
    </div>

    <!-- Error message -->
    <div v-else class="text-center text-danger">
      <p>{{ loadError ? "Impossible de charger ce patient." : "Patient introuvable." }}</p>
      <button v-if="loadError" class="btn btn-sm btn-outline-danger" @click="getPatient">
        Réessayer
      </button>
    </div>

  </div>
</template>

<script>
import api from '@/api';
import DonneesPatient from '@/components/patient/DonneesPatient.vue';

export default {

  components: { DonneesPatient },

  data() {
    return {
      patient: null,
      isLoading: true,
      loadError: false,

      tabs: [
        { name: 'informations', label: 'Information', route: 'information_personnelle', permission: 'VIEW_INFORMATION_PERSONNEL_PATIENT' },
        { name: 'assurances', label: 'Assurances', route: 'assurances_patient', permission: 'VIEW_ASSURANCE_PATIENT', module: 'ASSURANCES' },
        { name: 'consultations', label: 'Consultations', route: 'consultations_patient', permission: 'VIEW_SECTION_CONSULTATION_PATIENT' },
        { name: 'ordonnances', label: 'Ordonnances', route: 'ordonnances_patient', permission: 'VIEW_ORDONNANCE_PATIENT' },
        { name: 'analyses', label: 'Analyses', route: 'analyses_patient', permission: 'VIEW_ANALYSE_PATIENT' },
        { name: 'hospitalisations', label: 'Hospitalisations', route: 'hospitalisations_patient', permission: 'VIEW_HOSPITALISATION_PATIENT', module: 'HOSPITALISATION' },
        { name: 'constantes', label: 'Constantes', route: 'constantes_patient', permission: 'PATIENT_DOSSIER' },
        { name: 'documents', label: 'Documents', route: 'documents_patient', permission: 'DOCUMENTS_VOIR', module: 'DOCUMENTS' },
        { name: 'services', label: 'Autres services', route: 'services_patient', permission: 'VIEW_AUTRES_SERVICES_PATIENT' },
        { name: 'factures', label: 'Factures', route: 'factures_patient', permission: 'VIEW_AUTRES_SERVICES_FACTURE_PATIENT' },
      ],
    };
  },

  computed: {

    /* =======================================================
       PATIENT ID
       Dérivé directement de l'URL : reste toujours synchronisé
       même si on navigue d'un dossier patient à un autre sans
       que le composant soit recréé par Vue Router.
    ======================================================= */
    patientId() {
      return this.$route.params.id;
    },

    tabsAutorises() {
      return this.tabs.filter(tab =>
        (!tab.module || this.$store.getters.hasModule(tab.module)) &&
        this.$store.getters.hasPermission(tab.permission)
      );
    }

  },

  watch: {

    /* =======================================================
       Recharge le patient à chaque changement d'ID dans l'URL
    ======================================================= */
    patientId: {
      immediate: true,
      handler() {
        this.getPatient();
      }
    },

    tabsAutorises: {
      handler(tabs) {
        if (!tabs.length) return;

        const currentRoute = this.$route.name;

        const isRouteAutorisee = tabs.some(
          tab => tab.route === currentRoute
        );

        if (!isRouteAutorisee) {
          this.$router.replace({
            name: tabs[0].route,
            params: { id: this.patientId }
          });
        }
      }
    }

  },

  methods: {

    isActive(routeName) {
      return this.$route.name === routeName;
    },

    async getPatient() {

      this.isLoading = true;
      this.loadError = false;

      try {
        const res = await api.getItem('patients', this.patientId);
        this.patient = res.data;
      } catch (err) {
        console.error("Erreur récupération patient :", err);
        this.patient = null;
        this.loadError = true;
      } finally {
        this.isLoading = false;
      }
    },

  },
};
</script>

<style scoped>
/* === HEADER === */
.header-clinique {
  padding: 15px 20px;
  background-color: #e9f5ff;
  border-radius: 12px;
  margin-bottom: 20px;
}

/* === CARD === */
.card-clinique {
  background: #f8fafd;
  border-radius: 12px;
  border: none;
}

/* === ONGLET === */
.tabs-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.nav-clinique {
  display: flex;
  flex-wrap: wrap; /* s’adapte sur grand écran */
  border-bottom: 2px solid #dee2e6;
  gap: 5px;
}

.nav-clinique .nav-item {
  flex: 1 1 auto; /* largeur flexible */
  min-width: 120px;
  text-align: center;
}

.nav-clinique .nav-link {
  border: none;
  padding: 0.75rem 1.25rem;
  font-weight: 500;
  color: #495057;
  background: transparent;
  transition: all 0.3s ease;
  border-radius: 10px 10px 0 0;
}

.nav-clinique .nav-link:hover {
  background-color: #e9f5ff;
  color: #0d6efd;
}

.nav-clinique .nav-link.active {
  background-color: #fff;
  font-weight: 600;
  color: #0d6efd;
  border-bottom: 3px solid #0d6efd;
}

/* === RESPONSIVE === */
@media (max-width: 767px) {
  .header-clinique {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .btn-clinique {
    width: 100%;
  }

  /* Onglets scrollables horizontalement */
  .tabs-container {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
  }

  .nav-clinique {
    display: inline-flex;
    flex-wrap: nowrap; /* on empêche la casse de ligne */
  }

  .nav-clinique .nav-item {
    flex: 0 0 auto;
  }

  .nav-clinique .nav-link {
    font-size: 0.9rem;
    padding: 0.5rem 0.9rem;
  }
}
</style>